@if (newestEventUuid && newestEventUuid !== event.UUID) {
  <div class="top-alert">
    <div>
      <i class="fa-solid fa-diamond-exclamation"></i>
      Heads up: A new alarm event for this same {{event.ObjectType}} started after this event was cleared!
    </div>

    <div>
      <a
        [routerLink]="['../' + newestEventUuid]"
        class="button yellow"
      >
        See It Now
      </a>
    </div>
  </div>
}

@if (currentStage === 'unacked' || currentStage === 'acked') {
  <div class="top-alert">
    <div>
      <i class="fa-solid fa-diamond-exclamation"></i>
      {{interventionText}}
    </div>

    <div>
      @if (currentStage !== 'acked') {
        <button
          (click)="acknowledgeAlarm()"
          class="button yellow"
          [class.loading]="!userInterventionEnabled"
        >
          <span>
            Acknowledge
            <i class="fa-solid fa-check"></i>
          </span>
          <motn-spinner></motn-spinner>
        </button>
      }

      <button
        (click)="clearAlarm()"
        class="button green"
        [class.loading]="!userInterventionEnabled"
      >
        <span>
          Clear
          <i class="fa-solid fa-check-double"></i>
        </span>
        <motn-spinner></motn-spinner>
      </button>
    </div>
  </div>
}

@if (event) {
  <div class="details">
    <div class="stats">
      <div class="stat">
        <div>
          <div>
            {{event.ObjectType}} ID
          </div>
            <div>
              <motn-pill
                [squareLetter]="subjectTypeFirstLetter"
              >{{ event.ObjectID }}</motn-pill>
            </div>
        </div>

        @if (event.ObjectType === ObjectType.TILE && tile) {
          <div>
            <div>
              Tile Name
            </div>
            <div>
              {{tile.Name}}
            </div>
          </div>
        }

        <div>
          <div>
            Display Name
          </div>
            <div>
              {{objectName}}
            </div>
        </div>

        @if (isLorawanGatewayAlarm) {
          <div>
            <div>
              Gateway EUI
            </div>
            <div class="monospace">
              {{gatewayEUI}}
            </div>
          </div>
        }
      </div>

      <div class="stat">
        <h2>Times</h2>

        <div>
          <div>
            Started
          </div>
          <div class="monospace">
            {{makePrettyTime(event.StartTime)}}
          </div>
        </div>

        <div [class.yellow]="currentStage === 'acked'">
          <div>
            Acknowledged
          </div>
          <div class="monospace">
            {{event.AckTime ? makePrettyTime(event.AckTime) : ''}}
          </div>
        </div>

        <div [class.green]="currentStage === 'cleared'">
          <div>
            Cleared
          </div>
          <div class="monospace">
            {{event.ClearTime ? makePrettyTime(event.ClearTime) : ''}}
          </div>
        </div>
      </div>

      <div class="stat">
        <h2>People</h2>

        <div [class.yellow]="currentStage === 'acked'">
          <div>
            Acknowledged By
          </div>
          <div>
            <div>
              {{ackUser ? ackUser.Name : ''}}
            </div>
            @if (ackUser) {
              <motn-pill>{{ackUser.Username}}</motn-pill>
            }
          </div>
        </div>

        <div [class.green]="currentStage === 'cleared'">
          <div>
            Cleared By
          </div>
          <div>
            <div>
              {{event.ClearTime ? clearUser ? clearUser.Name : 'Nobody (auto-cleared)' : ''}}
            </div>
            @if (clearUser) {
              <motn-pill>{{clearUser.Username}}</motn-pill>
            }
          </div>
        </div>
      </div>
    </div>

    @if (!(event.ObjectType === ObjectType.DEVICE && event.ClearTime)) {
      <div class="object">
        <h2>
          Current State
        </h2>

        @if (tile) {
          <div>
            <motn-tile
              [rawTile]="tile"
              [hideAlarmMenu]="true"
            ></motn-tile>
          </div>
        }

        @if (device && newestValue?.Data && !event.ClearTime) {
          <div>
            <motn-stat-string
              [datum]="newestValue.Data.ReadErrors.toLocaleString()"
              [color]="'indigo'"
              [title]="'Read Failures'"
              [icon]="'upload fa-rotate-180'"
            ></motn-stat-string>

            <motn-stat-string
              [datum]="newestValue.Data.WriteErrors.toLocaleString()"
              [color]="'alt'"
              [title]="'Write Failures'"
              [icon]="'download fa-rotate-180'"
            ></motn-stat-string>
          </div>
        }

        @if (hub) {
          <div>
            @if (isLorawanGatewayAlarm) {
              @if (gatewayConnected === true) {
                <motn-stat-string
                  [datum]="'Connected'"
                  [color]="'green'"
                  [title]="'Gateway Status'"
                  [icon]="'cloud'"
                ></motn-stat-string>
              }
              @if (gatewayConnected !== true) {
                <motn-stat-string
                  [datum]="'Not Connected'"
                  [color]="'red'"
                  [title]="'Gateway Status'"
                  [icon]="'cloud-slash'"
                ></motn-stat-string>
              }
            } @else {
              @if (hubStatus && hubStatus.Success) {
                <motn-stat-string
                  [datum]="'Connected'"
                  [color]="'green'"
                  [title]="'Hub Status'"
                  [icon]="'cloud'"
                ></motn-stat-string>
              }

              @if (!hubStatus || !hubStatus.Success) {
                <motn-stat-string
                  [datum]="'Not Connected'"
                  [color]="'red'"
                  [title]="'Hub Status'"
                  [icon]="'cloud-slash'"
                ></motn-stat-string>
              }
            }
          </div>
        }
      </div>
    }
  </div>
}

@if (event?.ObjectType === ObjectType.TILE) {
  <div class="chart-container">
    <h2>
      Live Data Stream

      <button
        class="small"
        (click)="showMoreData()"
      >
        Show History
      </button>
    </h2>

    <div class="chart">
      @if (chartData$) {
        <motn-tile-history
          [datasets$]="chartData$"
        ></motn-tile-history>
      }
    </div>
  </div>
}

@if (shouldShowTable) {
  <motn-sort-table
    (pageNumberChanged$)="changePageNumber($event)"
    [table$]="table$"
    [paginationManagedByParent]="true"
    [autoScroll]="false"
  ></motn-sort-table>
}
